import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {useState} from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {NucleotideComponent, DEGENERATE_BASE} from 'molbio/monomers/MonomerConstants';
import {
  getBaseName,
  getNaturalBaseMonomer,
  getNaturalPhosphateMonomer,
  getNaturalSugarMonomer,
  getPhosphateName,
  getSugarName,
} from 'molbio/monomers/MonomersUtil';
import NucleotideImage from 'molbio/monomers/NucleotideImage';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';

type Monomer = MonomerQuery['monomers'][number];

export interface ModifiedNucleotideTooltipContentsProps {
  mod: ModifiedNucleotideAttributes | null;
  base: string;
  monomers: Array<Monomer>;
  nucleotideType: NucleotideType;
  showNucleotideStructure: boolean;
}

export default function ModifiedNucleotideTooltipContents({
  mod,
  base,
  monomers,
  nucleotideType,
  showNucleotideStructure,
}: ModifiedNucleotideTooltipContentsProps): JSX.Element {
  const [highlightComponent, setHighlightComponent] = useState<NucleotideComponent | null>(null);

  function handleMouseAction(group: NucleotideComponent | null): void {
    if (showNucleotideStructure) {
      setHighlightComponent(group);
    }
  }

  const sugarName = getSugarName(mod, monomers, nucleotideType);
  const phosphateName = getPhosphateName(mod, monomers);
  const baseName = getBaseName(mod, monomers, base);

  const sugarMonomer =
    monomers.find((monomer) => monomer.id && monomer.id === mod?.sugarMonomerId) ??
    getNaturalSugarMonomer(monomers, nucleotideType);

  const baseMonomerId = mod?.baseMonomerId;
  let baseMonomer: Monomer | typeof DEGENERATE_BASE;
  if (baseMonomerId === DEGENERATE_BASE) {
    baseMonomer = DEGENERATE_BASE;
  } else if (baseMonomerId) {
    baseMonomer = monomers.find((monomer) => monomer.id && monomer.id === baseMonomerId)!;
  } else {
    baseMonomer = getNaturalBaseMonomer(monomers, base) ?? DEGENERATE_BASE;
  }

  let phosphateMonomer: Monomer | null = null;
  if (mod) {
    if (mod.phosphateMonomerId) {
      phosphateMonomer = monomers.find((monomer) => monomer.id && monomer.id === mod.phosphateMonomerId)!;
    }
  } else {
    phosphateMonomer = getNaturalPhosphateMonomer(monomers);
  }

  return (
    <div className={css(styles.tooltip)}>
      <div className={css(styles.tooltipTable)}>
        {[
          {
            component: NucleotideComponent.SUGAR,
            label: 'Sugar',
            name: sugarName,
            archivePurpose: sugarMonomer?.archiveRecord?.purpose,
          },
          {
            component: NucleotideComponent.BASE,
            label: 'Base',
            name: baseName,
            archivePurpose: baseMonomer !== DEGENERATE_BASE && baseMonomer?.archiveRecord?.purpose,
          },
          {
            component: NucleotideComponent.PHOSPHATE,
            label: "3' Phosphate",
            name: phosphateName,
            archivePurpose: phosphateMonomer?.archiveRecord?.purpose,
          },
        ].map(({component, label, name, archivePurpose}) => (
          <React.Fragment key={component}>
            <strong
              className={highlightComponent === component ? css(styles.highlightedLabel) : ''}
              onMouseOver={() => {
                handleMouseAction(component);
              }}
              onMouseOut={() => {
                handleMouseAction(null);
              }}
            >
              {label}
            </strong>
            <span className={css(styles.monomerName, archivePurpose ? styles.archivedMonomerName : null)}>
              {archivePurpose ? `${name} (${archivePurpose})` : name}
            </span>
          </React.Fragment>
        ))}
      </div>
      {showNucleotideStructure && (
        <NucleotideImage
          sugar={sugarMonomer}
          base={baseMonomer}
          phosphate={phosphateMonomer}
          className={css(styles.chemicalStructureContainer)}
          svgClassName={css(styles.chemicalStructureSvg)}
          handleMouseAction={handleMouseAction}
          highlightComponent={highlightComponent}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  tooltip: {
    padding: 4,
    maxWidth: 250,
  },
  tooltipTable: {
    display: 'grid',
    gap: '1px 12px',
    gridTemplateColumns: 'auto 1fr',
  },
  monomerName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  archivedMonomerName: {
    color: GRAY_DARK,
  },
  highlightedLabel: {
    background: GRAY_LIGHT,
    margin: '-1px -4px',
    padding: '1px 4px',
  },
  chemicalStructureContainer: {
    textAlign: 'center',
    marginTop: 8,
    minWidth: 200,
    maxHeight: 160,
  },
  chemicalStructureSvg: {
    maxHeight: 'inherit',
    width: '100%',
  },
});
